import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { MagnifyingGlass, Books, ArrowRight, User, Stack } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function SeriesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const fetchSeries = async (p = 1, query = "", append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/public-series`, {
        params: {
          page: p,
          limit: 12,
          q: query.trim() || undefined
        }
      });
      if (append) {
        setItems(prev => [...(prev || []), ...data.series]);
      } else {
        setItems(data.series || []);
      }
      setMeta(data.meta);
    } catch (e) {
      setErr(e.response?.data?.error || "Couldn't load series.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchSeries(1, search, false);
      const params = {};
      if (search) params.q = search;
      setSearchParams(params);
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchSeries(next, search, true);
  };

  if (items === null && page === 1) return (
    <div className="max-w-5xl mx-auto px-6 py-20 text-muted-foreground animate-pulse">
      Loading collections...
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
        <div className="max-w-2xl">
          <div className="eyebrow text-muted-foreground mb-3 tracking-[0.2em]">COLLECTIONS</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-black tracking-tight">Series.</h1>
          <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
            Curated story arcs, multi-part essays, and thematic journals from the most thoughtful voices on Inkspot.
          </p>
        </div>
        <div className="w-full md:w-80">
          <div className="relative group">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-foreground transition-colors" />
            <Input
              placeholder="Search series title or author..."
              className="rounded-none pl-10 h-12 bg-card border-border focus-visible:ring-0 focus-visible:border-foreground transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {err ? <div className="text-sm text-destructive bg-destructive/10 p-4 border border-destructive/20 mb-8">{err}</div> : null}

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-20 text-center bg-card">
          <Stack size={48} weight="thin" className="mx-auto mb-4 opacity-20" />
          <div className="font-serif text-3xl font-black">No series found</div>
          <p className="mt-4 text-muted-foreground max-w-sm mx-auto">
            Try adjusting your search to find the collections you're looking for.
          </p>
          <Button variant="outline" onClick={() => setSearch("")} className="mt-8 rounded-none px-8">Reset search</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {items.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.1, 0.4) }}
            >
              <Link 
                to={`/${s.author.username}/series/${s.slug}`}
                className="group block h-full border border-border bg-card lift-card flex flex-col"
              >
                <div className="aspect-[16/10] bg-secondary overflow-hidden relative border-b border-border">
                  {s.cover_image ? (
                    <img src={fileUrl(s.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full grid place-items-center opacity-10"><Books size={80} weight="thin" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-4 right-4 bg-foreground text-background text-[10px] font-black uppercase px-3 py-1.5 shadow-lg tracking-widest translate-y-2 group-hover:translate-y-0 transition-transform">
                    {s.articles_count} Chapters
                  </div>
                </div>

                <div className="p-6 flex-grow">
                  <div className="flex items-center gap-2 mb-4">
                    {s.author.profile_picture ? (
                      <img src={fileUrl(s.author.profile_picture)} alt="" className="h-5 w-5 rounded-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                    ) : (
                      <div className="h-5 w-5 bg-secondary rounded-full grid place-items-center text-[8px] font-bold">{(s.author.name || "A")[0]}</div>
                    )}
                    <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground group-hover:text-foreground transition-colors">{s.author.name || s.author.username}</span>
                  </div>
                  
                  <h2 className="font-serif text-2xl font-black leading-tight group-hover:underline underline-offset-4 decoration-1">{s.title}</h2>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-3 leading-relaxed font-serif italic">
                    {s.description || "A cohesive journey through multiple chapters."}
                  </p>
                </div>

                <div className="px-6 pb-6 pt-2 mt-auto flex items-center justify-between border-t border-border/30">
                  <span className="text-[10px] eyebrow text-muted-foreground group-hover:text-foreground transition-colors">Start Reading</span>
                  <ArrowRight size={18} className="text-muted-foreground group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {meta?.has_more && (
        <div className="mt-20 text-center">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loading}
            className="rounded-none px-16 h-14 font-serif text-lg font-bold border-2 hover:bg-foreground hover:text-background transition-all"
          >
            {loading ? "Finding more series..." : "Load more collections"}
          </Button>
        </div>
      )}
    </div>
  );
}
